import { useEffect, useRef, useState } from 'react';

import { api, ApiError, type Restaurant } from './api';

/**
 * Put a restaurant's guest menu on its own WhatsApp number.
 *
 * Every restaurant starts on Scandeer's fleet number: one number, every
 * restaurant's table cards, the hub answering all of them. That works and
 * stays the default. What it costs the restaurant is that the conversation
 * belongs to Scandeer — the guest's chat history is with us, not with them,
 * and so is the Meta bill.
 *
 * This panel is the other option. The owner onboards the WhatsApp number they
 * already run their business on, their table cards start carrying it, and
 * their guests end up in their own chat list.
 *
 * ── Why coexistence ───────────────────────────────────────────────────────
 *
 * The number a restaurant wants to use is almost always the one on their
 * signboard, already in the WhatsApp Business app on somebody's phone, with
 * years of customer chats in it. Ordinary Cloud API onboarding takes that
 * number OVER: the app stops working on it. No owner will agree to that to
 * gain a menu link, and the ones who agree without understanding it will call
 * us the same evening.
 *
 * Coexistence onboarding — `featureType: 'coexistence'` — leaves the mobile
 * app working while the Cloud API shares the number. The owner keeps their
 * phone. We get the webhook.
 *
 * The consequence to be honest about: an inbound message reaches BOTH. A
 * guest sending `menu …` gets our automatic reply, and the owner sees the
 * message in their app too and may answer it by hand. That is not a bug to
 * design out — it is what sharing a number means — but it is why the stored
 * config records that a number came through this flow.
 *
 * ── Why this panel came back ──────────────────────────────────────────────
 *
 * It existed once, for WhatsApp DELIVERY ordering, and was removed in 4fae4fa
 * because Scandeer owns delivery: it set up a Meta number for orders that
 * arrive through none of it. That reasoning was right and does not reach
 * here. The guest menu is a real use for a restaurant's own number, and the
 * credential endpoint behind this panel was never removed — it has been the
 * only way to set these values, with no UI, ever since.
 *
 * See docs/WHATSAPP-GUEST-MENU.md §9.
 */

/** Meta's session info comes back in more shapes than its docs admit. */
function parseMetaPayload(raw: unknown) {
  if (!raw) return null;
  let d: any = raw;
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d);
    } catch {
      return null;
    }
  }
  if (typeof d !== 'object' || !d) return null;

  const t = d.data || d.sessionInfo || d.session_info || d.payload || d.response || d;

  const phoneId = t.phone_number_id || t.phoneNumberId || t.phone_id || d.phone_number_id;
  const wabaId = t.waba_id || t.wabaId || t.waba_account_id || d.waba_id || d.wabaId;
  const code = t.code || t.auth_code || d.code;
  const accessToken = t.access_token || t.accessToken || d.access_token;

  if (!phoneId && !wabaId && !code && !accessToken) return null;
  return {
    phoneNumberId: phoneId ? String(phoneId) : undefined,
    wabaId: wabaId ? String(wabaId) : undefined,
    code: code ? String(code) : undefined,
    accessToken: accessToken ? String(accessToken) : undefined,
  };
}

const inputStyle = {
  width: '100%',
  padding: '10px 14px',
  fontSize: 14,
  borderRadius: 6,
  background: 'var(--surface-1)',
  border: '1px solid var(--line)',
  color: '#fff',
} as const;

const labelStyle = {
  display: 'block',
  fontSize: 13,
  fontWeight: 700,
  marginBottom: 4,
} as const;

export function WhatsAppConnectPanel({
  restaurant,
  onConnected,
}: {
  restaurant: Restaurant;
  onConnected: () => Promise<void>;
}) {
  const cfg = restaurant.whatsapp_config;
  // The display number, not the phone number id. A config holding the id
  // without the number is onboarded but cannot print a card — see
  // guestMenuNumber() in the worker — so that is not "connected" here either.
  const connected = !!cfg?.displayNumber;

  const [open, setOpen] = useState(!connected);
  const [showManual, setShowManual] = useState(false);
  const [phoneNumberId, setPhoneNumberId] = useState(cfg?.phoneNumberId || '');
  const [wabaId, setWabaId] = useState(cfg?.wabaId || '');
  // Never prefilled: the server redacts stored tokens to `hasAccessToken`, so
  // a resubmitted form arrives without one. Blank means unchanged, not erase.
  const [accessToken, setAccessToken] = useState('');
  // Prefilled from what is stored, because this one IS sent back to the page
  // — it is printed on every table card, so it is not a secret.
  const [displayNumber, setDisplayNumber] = useState(cfg?.displayNumber || '');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  /**
   * A save that stored something but moved no number.
   *
   * Not an error — the ids are on file and the fallback kept the cards
   * working — but emphatically not a success either, and it was being shown
   * in the same green banner as a real connection. Guests saw no change and
   * the operator had no way to tell that from "done".
   */
  const [inert, setInert] = useState('');
  const [err, setErr] = useState('');
  const [meta, setMeta] = useState<{ appId: string; configId: string } | null>(null);

  // The popup and the `message` listener outlive a render, and both have to be
  // torn down if this panel goes away mid-flow — a listener left behind would
  // save credentials against a restaurant nobody is looking at any more.
  const listener = useRef<((e: MessageEvent) => void) | null>(null);
  const poll = useRef<ReturnType<typeof setInterval> | null>(null);
  /**
   * What Meta has told us so far, across its two channels.
   *
   * The onboarding reports itself twice and each half is useless alone: the
   * `message` event carries the phone number id and the WABA id, and the
   * `FB.login` callback carries the code that becomes the access token. Saving
   * on whichever arrives first writes a config with ids and no token, and then
   * the other overwrites it with a token and no ids.
   *
   * So the halves are collected here and saved once, together.
   */
  const session = useRef<{
    phoneNumberId?: string;
    wabaId?: string;
    code?: string;
    accessToken?: string;
  }>({});

  useEffect(() => {
    setPhoneNumberId(cfg?.phoneNumberId || '');
    setWabaId(cfg?.wabaId || '');
    setDisplayNumber(cfg?.displayNumber || '');
    setOpen(!cfg?.displayNumber);
  }, [restaurant.id, cfg?.phoneNumberId, cfg?.wabaId, cfg?.displayNumber]);

  useEffect(
    () => () => {
      if (listener.current) window.removeEventListener('message', listener.current);
      if (poll.current) clearInterval(poll.current);
    },
    [],
  );

  // Fetched rather than compiled in: the pair were hardcoded in this file the
  // first time round, and an app id in a source file outlives the app it
  // belonged to. A missing configId is reported here rather than inside
  // Meta's popup, where nothing we can see says why it failed.
  useEffect(() => {
    let live = true;
    void api
      .metaConfig()
      .then((m) => {
        if (live) setMeta(m);
      })
      .catch(() => {
        if (live) setMeta({ appId: '', configId: '' });
      });
    return () => {
      live = false;
    };
  }, []);

  async function save(input: {
    phoneNumberId?: string;
    wabaId?: string;
    code?: string;
    accessToken?: string;
    displayNumber?: string;
    coexistence?: boolean;
  }) {
    setBusy(true);
    setErr('');
    setInert('');
    setMsg('Saving, and asking Meta which number this is…');
    try {
      const res = await api.connectWhatsApp(restaurant.id, input);
      // `connected` is the server saying whether a guest would notice. Only
      // then is the panel closed — a save that changed nothing leaves the
      // form open, because there is more to do.
      if (res.connected) {
        setMsg(res.message);
        setInert('');
        setAccessToken('');
        setOpen(false);
      } else {
        setMsg('');
        setInert(res.message);
      }
      await onConnected();
    } catch (e) {
      setMsg('');
      setErr(e instanceof ApiError ? e.message : 'Could not save the WhatsApp connection');
    } finally {
      setBusy(false);
    }
  }

  function loadFacebookSdk(): Promise<void> {
    return new Promise((resolve, reject) => {
      if ((window as any).FB) return resolve();
      (window as any).fbAsyncInit = () => {
        (window as any).FB.init({
          appId: meta?.appId,
          autoLogAppEvents: true,
          xfbml: true,
          version: 'v22.0',
        });
        resolve();
      };
      const script = document.createElement('script');
      script.id = 'facebook-jssdk';
      script.src = 'https://connect.facebook.net/en_US/sdk.js';
      script.async = true;
      script.defer = true;
      script.onerror = () => reject(new Error('Facebook SDK did not load'));
      document.body.appendChild(script);
    });
  }

  async function launchOnboarding() {
    if (!meta?.appId || !meta?.configId) {
      setErr(
        'This portal has no Meta coexistence configuration set (META_APP_ID / META_CONFIG_ID), ' +
          'so the onboarding popup cannot run. Ask Scandeer to set them, or enter the ids by hand.',
      );
      return;
    }

    setBusy(true);
    setErr('');
    setMsg('Opening Meta onboarding…');
    session.current = {};

    // Registered before the popup opens: a fast flow can post its session
    // info back before a listener added afterwards would exist.
    //
    // This only collects. The save happens in the FB.login callback below,
    // once the code is in hand too — except on the popup fallback, where
    // there is no callback and this is all we ever get.
    const onMessage = (event: MessageEvent) => {
      const parsed = parseMetaPayload(event.data);
      if (!parsed) return;
      session.current = { ...session.current, ...parsed };
    };
    listener.current = onMessage;
    window.addEventListener('message', onMessage);

    const stopListening = () => {
      window.removeEventListener('message', onMessage);
      listener.current = null;
    };

    // The SDK first. `response_type: 'code'` with the override is what makes
    // FB.login hand back an exchangeable code instead of a browser token the
    // server cannot use; the exchange happens in the worker, which has the
    // app secret.
    try {
      await loadFacebookSdk();
      if ((window as any).FB) {
        (window as any).FB.login(
          async (response: any) => {
            stopListening();
            const code = response?.authResponse?.code;
            const got = session.current;
            if (code || got.phoneNumberId || got.wabaId) {
              // One save, both halves. A code with no ids is still worth
              // sending: the worker exchanges it for a token and can then
              // look the rest up from a WABA id, and failing that the panel
              // says what is still missing.
              await save({ ...got, code: code || got.code, coexistence: true });
            } else {
              // Cancelled, or the number is already claimed by another app.
              // Either way nothing arrived, and saying so beats a spinner.
              setBusy(false);
              setMsg('');
              setErr(
                'Meta did not return a number. If the onboarding window was closed, try again; ' +
                  'if it finished, enter the Phone Number ID by hand below.',
              );
            }
          },
          {
            config_id: meta.configId,
            response_type: 'code',
            override_default_response_type: true,
            extras: {
              sessionInfoVersion: '3',
              // The whole point: the owner keeps the WhatsApp app on this
              // number. Without it Meta migrates the number and their phone
              // stops working on it.
              featureType: 'coexistence',
            },
          },
        );
        return;
      }
    } catch (e) {
      console.warn('[Meta SDK unavailable, falling back to a plain popup]', e);
    }

    // Fallback for a browser that blocks connect.facebook.net. Same flow and
    // the same extras, driven by a window rather than the SDK.
    const extras = JSON.stringify({
      sessionInfoVersion: '3',
      featureType: 'coexistence',
      version: 'v4',
    });
    const url =
      'https://business.facebook.com/messaging/whatsapp/onboard/' +
      `?app_id=${encodeURIComponent(meta.appId)}` +
      `&config_id=${encodeURIComponent(meta.configId)}` +
      `&extras=${encodeURIComponent(extras)}`;
    const popup = window.open(url, 'MetaWhatsAppOnboard', 'width=650,height=750,scrollbars=yes');
    if (!popup) {
      stopListening();
      setBusy(false);
      setMsg('');
      setErr('The browser blocked the popup. Allow popups for this site and try again.');
      return;
    }

    // No FB.login callback on this path, so the window closing is the only
    // signal there is. Whatever Meta posted before it closed is all we get.
    poll.current = setInterval(() => {
      if (!popup.closed) return;
      if (poll.current) clearInterval(poll.current);
      poll.current = null;
      stopListening();

      const got = session.current;
      if (got.phoneNumberId || got.wabaId || got.code) {
        void save({ ...got, coexistence: true });
        return;
      }
      // Closed with nothing posted. That is not necessarily failure — the
      // operator may have finished in a flow that reports out of band — so
      // the panel reloads to surface a connection that DID land, and offers
      // the manual route for when one did not.
      setBusy(false);
      setMsg('');
      void onConnected();
      setShowManual(true);
    }, 1000);
  }

  async function saveManual(e: React.FormEvent) {
    e.preventDefault();
    await save({
      phoneNumberId: phoneNumberId.trim() || undefined,
      wabaId: wabaId.trim() || undefined,
      accessToken: accessToken.trim() || undefined,
      displayNumber: displayNumber.trim() || undefined,
      coexistence: true,
    });
  }

  async function switchCards(to: 'whatsapp' | 'menu') {
    setBusy(true);
    setErr('');
    setMsg('');
    try {
      await api.updateRestaurant(restaurant.id, { guestQr: to });
      setMsg(
        to === 'whatsapp'
          ? 'Table codes now open WhatsApp on this number. Reprint them from the restaurant’s Floor QR screen.'
          : 'Table codes now open the menu directly.',
      );
      await onConnected();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : 'Could not change how the table codes work');
    } finally {
      setBusy(false);
    }
  }

  const onWhatsApp = restaurant.guest_qr === 'whatsapp';

  return (
    <section className="card connect" style={{ marginTop: 24 }}>
      <div className="pane-head">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span>💬</span> Guest Menu WhatsApp Number
        </h2>
        <span className="spacer" />
        {connected && (
          <button className="btn sm" onClick={() => setOpen((v) => !v)}>
            {open ? 'Close' : '⚙️ Manage'}
          </button>
        )}
      </div>

      {msg && <div className="banner ok">{msg}</div>}
      {/* Amber, not green. It saved; it did nothing a guest can see. */}
      {inert && (
        <div
          className="banner"
          style={{
            background: 'rgba(245,158,11,0.12)',
            color: '#f59e0b',
            borderColor: 'rgba(245,158,11,0.35)',
            lineHeight: 1.55,
          }}
        >
          <strong>Saved, but the number has not moved.</strong> {inert}
        </div>
      )}
      {err && <div className="banner bad">{err}</div>}

      {connected && !open && (
        <div className="banner ok" style={{ display: 'grid', gap: 4 }}>
          <div>
            <strong>Guests message {cfg?.displayNumber}.</strong>{' '}
            {onWhatsApp
              ? 'This number is on the printed table cards.'
              : 'Table codes still open the menu directly — switch them below.'}
          </div>
          <div style={{ fontSize: 12, opacity: 0.85 }}>
            Phone Number ID <span className="mono">{cfg?.phoneNumberId}</span>
            {cfg?.wabaId && (
              <>
                {' · '}WABA <span className="mono">{cfg.wabaId}</span>
              </>
            )}
            {cfg?.coexistence && ' · coexistence: the mobile app keeps working'}
          </div>
        </div>
      )}

      {/* Only when the portal is the one managing this number.

          The subscription fails silently when it fails — the number is real,
          the panel looks connected, every guest gets nothing — so it is worth
          a banner. But the portal only subscribes a WABA it has a token for.
          A number already onboarded for Scandeer is carried by the hub, whose
          own config and subscription the portal knows nothing about, and
          telling an operator their webhook is broken because WE did not set
          it up sends them chasing a problem that is not there. */}
      {connected && cfg?.hasAccessToken && !cfg?.subscribed && (
        <div className="banner bad" style={{ fontSize: 13, lineHeight: 1.55 }}>
          Meta has not confirmed a webhook subscription for this WABA, so messages to this number
          will not reach us and guests will get no reply. Press <strong>Save</strong> again to
          retry it, and leave the table codes on the direct menu link until it clears.
        </div>
      )}
      {connected && !cfg?.hasAccessToken && (
        <p className="hint" style={{ marginTop: 10, lineHeight: 1.55 }}>
          This number is carried by Scandeer's own WhatsApp setup rather than by a connection made
          here, so the portal holds no token for it and does not manage its webhook. That is the
          normal arrangement for a number already onboarded for Scandeer — guests reach the hub
          through the same route the fleet number uses.
        </p>
      )}

      {(!connected || open) && (
        <>
          <p className="hint" style={{ marginTop: 0, lineHeight: 1.55 }}>
            Leave this alone and the restaurant's table cards use Scandeer's own WhatsApp number,
            which needs no setup and is the default. Connect a number here only when the
            restaurant wants its guests in <em>its</em> chat list and on its own Meta bill.
          </p>

          <div
            className="banner info"
            style={{
              background: 'rgba(56,189,248,0.1)',
              color: '#38bdf8',
              borderColor: 'rgba(56,189,248,0.25)',
              fontSize: 13,
              lineHeight: 1.55,
            }}
          >
            Onboarding runs in <strong>coexistence</strong> mode, so the WhatsApp Business app on
            the owner's phone keeps working on this number — their chats and contacts stay where
            they are. Messages arrive in <em>both</em> places: a guest sending a menu code gets
            the automatic reply, and the owner sees the message in their app as well.
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', margin: '16px 0' }}>
            <button
              type="button"
              className="btn primary"
              disabled={busy || !meta}
              onClick={launchOnboarding}
              style={{ padding: '10px 20px', fontWeight: 700 }}
            >
              {busy ? 'Working…' : '⚡ Connect a WhatsApp number'}
            </button>
            <button
              type="button"
              className="btn sm"
              onClick={() => setShowManual((v) => !v)}
              style={{ padding: '10px 16px' }}
            >
              {showManual ? 'Hide manual entry' : 'Enter ids by hand'}
            </button>
          </div>

          {showManual && (
            <form onSubmit={saveManual} style={{ display: 'grid', gap: 12, maxWidth: 460 }}>
              <div className="hint" style={{ margin: 0, lineHeight: 1.55 }}>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>A number already onboarded for Scandeer</strong> needs only the number
                  itself. The hub already holds its credentials and already receives its messages;
                  all the portal does with the number is print it on the cards and recognise
                  guests arriving on it. Leave the ids and the token blank.
                </p>
                <p style={{ margin: 0 }}>
                  <strong>A number new to us</strong> needs the Phone Number ID and a token, so
                  Meta can be asked what the number is and the webhook can be subscribed — or use
                  “Connect a WhatsApp number” above, which does both.{' '}
                  {cfg?.hasAccessToken && 'A token is on file; leave that box blank to keep it.'}
                </p>
              </div>
              <div>
                <label style={labelStyle} htmlFor="wa-pid">
                  Phone Number ID
                </label>
                <input
                  id="wa-pid"
                  style={inputStyle}
                  className="mono"
                  value={phoneNumberId}
                  onChange={(e) => setPhoneNumberId(e.target.value)}
                  placeholder="15 digits, from WhatsApp Manager → API Setup"
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="wa-waba">
                  WABA ID <span style={{ fontWeight: 400, opacity: 0.7 }}>(optional)</span>
                </label>
                <input
                  id="wa-waba"
                  style={inputStyle}
                  className="mono"
                  value={wabaId}
                  onChange={(e) => setWabaId(e.target.value)}
                  placeholder="Leave blank to keep what is stored"
                />
              </div>
              <div>
                <label style={labelStyle} htmlFor="wa-num">
                  WhatsApp number{' '}
                  <span style={{ fontWeight: 400, opacity: 0.7 }}>(what guests dial)</span>
                </label>
                <input
                  id="wa-num"
                  style={inputStyle}
                  className="mono"
                  value={displayNumber}
                  onChange={(e) => setDisplayNumber(e.target.value)}
                  placeholder="919876543210 — country code first, no +"
                />
                <p className="hint" style={{ margin: '4px 0 0', lineHeight: 1.5 }}>
                  This is the number printed on the table cards. It must match the number in
                  WhatsApp Manager <em>exactly</em> — guest messages are matched against it, and a
                  wrong one means every guest gets no reply at all. With a token on file, Meta's
                  own answer replaces whatever is typed here.
                </p>
              </div>
              <div>
                <label style={labelStyle} htmlFor="wa-token">
                  Access token{' '}
                  <span style={{ fontWeight: 400, opacity: 0.7 }}>
                    {cfg?.hasAccessToken ? '(blank keeps the one on file)' : '(required)'}
                  </span>
                </label>
                <input
                  id="wa-token"
                  type="password"
                  autoComplete="off"
                  style={inputStyle}
                  value={accessToken}
                  onChange={(e) => setAccessToken(e.target.value)}
                  placeholder="System User token with whatsapp_business_management"
                />
                <p className="hint" style={{ margin: '4px 0 0' }}>
                  A permanent System User token is the one to use here. It is stored on the server
                  and never sent back to this page.
                </p>
              </div>
              <button
                className="btn primary"
                // Ids alone, with no token and no typed number, would save
                // and change nothing. Either one makes the save worth making.
                disabled={
                  busy ||
                  (!displayNumber.trim() && !accessToken.trim() && !cfg?.hasAccessToken)
                }
                style={{ padding: '10px 24px', justifySelf: 'start' }}
              >
                {busy ? 'Saving…' : 'Save'}
              </button>
            </form>
          )}
        </>
      )}

      {/* Only once there is a number to point them at. Offering this with no
          number would print cards for a number that does not exist, which is
          what the worker refuses outright. */}
      {connected && (
        <div style={{ marginTop: 16 }}>
          <button
            type="button"
            className={`btn sm ${onWhatsApp ? '' : 'primary'}`}
            disabled={busy}
            onClick={() => switchCards(onWhatsApp ? 'menu' : 'whatsapp')}
          >
            {onWhatsApp
              ? 'Table codes: back to the direct menu link'
              : '💬 Put this number on the table codes'}
          </button>
        </div>
      )}
    </section>
  );
}
